import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/** A card on `--surface` (docs/DESIGN.md: radius 24, 1px line border, no shadow). */
export function Card({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn('rounded-3xl border border-line bg-surface p-5 text-fg', className)}
      {...props}
    />
  );
}

/** Section title, 15/600. */
export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-[15px] font-semibold', className)} {...props} />;
}

/** Uppercase section label above a list group, 13/600, muted. */
export function SectionLabel({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn(
        'px-1 text-[13px] font-semibold tracking-[0.4px] text-muted uppercase',
        className,
      )}
      {...props}
    />
  );
}

/** A list group: one surface card whose rows are separated by 1px lines (radius 20). */
export function ListGroup({ className, ...props }: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      className={cn(
        'flex flex-col divide-y divide-line overflow-hidden rounded-[20px] border border-line bg-surface',
        className,
      )}
      {...props}
    />
  );
}

/** The 40px square on `--surface-2` at the start of a list row (an icon or an initial). */
export function IconSquare({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-[14px] bg-surface-2 text-[15px] font-semibold text-fg',
        className,
      )}
      {...props}
    />
  );
}

/** Classes for a tappable list row (a Link or button inside a ListGroup). */
export const listRowClass =
  'flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent';
